import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './CustomToastStyles.css';
import useAuth from './useAuth.js';
import Header from './Header.js';
import Sidebar from './Sidebar.js';
import Footer from './Footer.js';
import Select from 'react-select';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Add_LocalZMConveyance = () => {
  useAuth(); // ✅ Custom authentication hook

  // ✅ State
  const [selectedRole, setSelectedRole] = useState(null);
  const [empCodes, setEmpCodes] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [empDetails, setEmpDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [duration, setDuration] = useState(null);
  const [visitRanges, setVisitRanges] = useState([]);
  const [visits, setVisits] = useState({});
  const [branches, setBranches] = useState([]); // State to hold branch options
  const [selectedBranches, setSelectedBranches] = useState([]); // State to hold selected branches
  const [amount, setAmount] = useState(''); // State for the single amount field

  // ✅ Dropdown options
  const roleOptions = [
    { value: 'cluster_manager', label: 'Cluster Manager' },
    { value: 'zonal_manager', label: 'Zonal Manager' },
    { value: 'ops_general_manager', label: 'Ops General Manager' },
  ];

  const durationOptions = [
    { value: '15', label: '15 Days' },
    { value: '30', label: '30 Days' },
  ];

  // ✅ Fetch employee codes
  useEffect(() => {
    const fetchEmployeeCodes = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}getempcodes`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        const result = await response.json();
        if (result.status) {
          const formattedEmpCodes = result.emp_codes.map(emp => ({
            value: emp.emp_code,
            label: emp.emp_code
          }));
          setEmpCodes(formattedEmpCodes);
        } else {
          toast.error('Failed to fetch employee codes');
        }
      } catch (error) {
        console.error('Failed to fetch employee codes', error);
        toast.error('Failed to fetch employee codes');
      }
    };
    fetchEmployeeCodes();
  }, []);

  // ✅ Fetch employee details when employee code is selected
  useEffect(() => {
    const fetchEmployeeDetails = async () => {
      if (!selectedEmp) return; // Do nothing if no employee is selected
      try {
        setLoading(true);
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}getemployeedetails/${selectedEmp.value}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        const result = await response.json();
        if (result.status && result.data) {
          setEmpDetails(result.data);
        } else {
          setEmpDetails(null);
          toast.error('Employee details not found');
        }
      } catch (error) {
        console.error('Error fetching employee details', error);
        toast.error('Failed to fetch employee details');
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeDetails();
  }, [selectedEmp]); // Only run when selectedEmp changes

  // ✅ Fetch branches from API
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}getBranches`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        const result = await response.json();
        if (result.status) {
          const formattedBranches = result.data.map(branch => ({
            value: branch.SysNo, // assuming `SysNo` is unique for each branch
            label: `${branch.SysField}`, // assuming `SysField` is the branch name
          }));
          setBranches(formattedBranches);
        } else {
          toast.error('Failed to fetch branches');
        }
      } catch (error) {
        console.error('Failed to fetch branches', error);
        toast.error('Failed to load branches');
      }
    };

    fetchBranches();
  }, []); // Fetch branches on component mount

  // ✅ Handle Duration Change
  const handleDurationChange = (selected) => {
    setDuration(selected);
    setVisitRanges([]); // Reset visit ranges when changing duration

    if (selected?.value === '15') {
      setVisitRanges(['1_15', '16_30']); // Set two ranges for 15 days
    } else if (selected?.value === '30') {
      setVisitRanges(['1_30']); // Set one range for 30 days
    }
    setVisits({}); // Reset visits input
  };

  const handleVisitChange = (range, value) => {
    setVisits(prev => ({
      ...prev,
      [range]: value
    }));
  };

  const handleBranchChange = (selected) => {
    setSelectedBranches(selected); // Update the selected branches
  };

  const handleAmountChange = (e) => {
    setAmount(e.target.value);
  };

  const handleBack = () => {
    // Redirect or go back
    window.history.back();
  };

  // ✅ Check if submit button should be enabled
  const isSubmitDisabled = !(
    selectedRole &&
    selectedEmp &&
    amount !== ''
  );

  const handleSubmit = async () => {
    try {
      const formData = {
        emp_code: selectedEmp.value,
        comp_name: empDetails.comp_name,
        role: selectedRole.value,
        duration: duration.value,
        visits,
        amount: amount,
        // Ensure branches are correctly formatted
         branches: selectedBranches.map(branch => branch.value), // Only extract the values

      };

      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}conveyance_master`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();
      if (result.status) {
        toast.success('Form submitted successfully!');
      } else {
        toast.error('Failed to submit form');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('An error occurred while submitting the form');
    }
  };

  return (
    <div className="hold-transition sidebar-mini layout-fixed" style={{ fontSize: '12px' }}>
      <div className="wrapper">
        <Header />
        <Sidebar />

        <div className="content-wrapper">
          {/* ✅ Header */}
          <div className="content-header">
            <div className="container-fluid">
              <div className="row mb-2">
                <div className="col-sm-6">
                  <h1 className="m-0" style={{ fontSize: '20px' }}>Local Conveyance Form</h1>
                </div>
                <div className="col-sm-6">
                  <ol className="breadcrumb float-sm-right">
                    <li className="breadcrumb-item"><a href="/dashboard">Home</a></li>
                    <li className="breadcrumb-item active">Local Conveyance Form</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          {/* ✅ Form Section */}
          <div className="content">
            <div className="container-fluid">
              <div className="card p-3" style={{ maxWidth: '450px' }}>
                {/* Manager Type */}
                <label style={{ fontWeight: 'bold', marginBottom: '5px' }}>Select Manager Type</label>
                <Select
                  options={roleOptions}
                  value={selectedRole}
                  onChange={setSelectedRole}
                  placeholder="Select Manager Type"
                  isClearable
                />

                {/* Employee Code Search */}
                <label style={{ fontWeight: 'bold', marginTop: '15px', marginBottom: '5px' }}>Search Employee Code</label>
                <Select
                  options={empCodes}
                  value={selectedEmp}
                  onChange={setSelectedEmp}
                  placeholder="Type or select employee code"
                  isClearable
                  isSearchable
                />

                {/* Employee Details */}
                {selectedEmp && empDetails && (
                  <div className="mt-3">
                    <h6 style={{ fontWeight: 'bold', marginBottom: '10px' }}>Employee Details</h6>
                    <table className="table table-sm table-bordered" style={{ fontSize: '12px' }}>
                      <tbody>
                        <tr><th>Employee Code</th><td>{empDetails.emp_code}</td></tr>
                        <tr><th>Name</th><td>{empDetails.comp_name}</td></tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Duration Dropdown */}
                <label style={{ fontWeight: 'bold', marginTop: '15px', marginBottom: '5px' }}>Select Duration</label>
                <Select
                  options={durationOptions}
                  value={duration}
                  onChange={handleDurationChange}
                  placeholder="Select 15 or 30 Days"
                  isClearable
                />

                {/* Visit Ranges */}
                {duration && visitRanges.length > 0 && visitRanges.map(range => (
                  <div key={range} style={{ marginTop: '10px' }}>
                    <label style={{ fontWeight: 'bold' }}>
                      Visits for {range} days:
                    </label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={visits[range] || ''}
                      onChange={(e) => handleVisitChange(range, e.target.value)}
                      style={{ width: '100px', marginTop: '5px' }}
                    />
                  </div>
                ))}

                {/* Amount Input */}
                <label style={{ fontWeight: 'bold', marginTop: '15px', marginBottom: '5px' }}>Enter Amount</label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  value={amount}
                  onChange={handleAmountChange}
                  placeholder="Enter amount"
                />

                {/* Branch Selection */}
                <label style={{ fontWeight: 'bold', marginTop: '15px', marginBottom: '5px' }}>
                  Select Branches
                </label>
                <Select
                  isMulti
                  options={branches}
                  value={selectedBranches}
                  onChange={handleBranchChange}
                  placeholder="Select Branches"
                  isClearable
                />

                {/* Submit and Back Buttons */}
                <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between' }}>
                  <button className="btn btn-secondary btn-sm" onClick={handleBack}>Back</button>
                  <button className="btn btn-primary btn-sm" onClick={handleSubmit} disabled={isSubmitDisabled}>Submit</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>

      <ToastContainer />
    </div>
  );
};

export default Add_LocalZMConveyance;
